import type { FrozenDeliveryQuoteSummary } from "./frozen-delivery-quote";
import type { MediaAsset } from "./storefront-types";

export const DEFAULT_FROZEN_DELIVERY_CALCULATOR_EYEBROW =
  "UK frozen delivery";
export const DEFAULT_FROZEN_DELIVERY_CALCULATOR_HEADLINE =
  "Estimate how many frozen boxes your order needs.";
export const DEFAULT_FROZEN_DELIVERY_CALCULATOR_IMAGE_ALT =
  "Open insulated frozen delivery box";
export const FROZEN_DELIVERY_CALCULATOR_PRICE_TOKEN = "{{pricePerBox}}";
export const DEFAULT_FROZEN_DELIVERY_CALCULATOR_BODY_HTML = [
  "<p>We deliver chilled and frozen Nigerian food to any address in the UK. The delivery price is the same wherever you live in the United Kingdom.</p>",
  `<p>Delivery is calculated by the number of frozen boxes needed for your order. Every item takes up a different amount of space, and currently each box costs <span class="font-semibold text-slate-950">${FROZEN_DELIVERY_CALCULATOR_PRICE_TOKEN}</span> to deliver.</p>`,
  "<p>Select example products and this tool will estimate how many boxes your items fill, plus the delivery cost before checkout.</p>",
].join("");

type FrozenDeliveryCalculatorIntroSource = {
  bodyHtml?: string | null;
  eyebrow?: string | null;
  headline?: string | null;
  image?: MediaAsset | null;
};

type FrozenDeliveryCalculatorIntro = {
  bodyHtml: string;
  eyebrow: string;
  headline: string;
  image: MediaAsset;
};

function normalizeOptionalText(value: string | null | undefined) {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeMediaAsset(asset: MediaAsset | null | undefined) {
  if (!asset) {
    return null;
  }

  const url = normalizeOptionalText(asset.url);
  if (!url) {
    return null;
  }

  return {
    url,
    alt: normalizeOptionalText(asset.alt) ?? "",
  };
}

export function populateFrozenDeliveryCalculatorBodyHtml(
  bodyHtml: string | null | undefined,
  pricePerBoxLabel: string | null,
) {
  const resolvedBodyHtml =
    normalizeOptionalText(bodyHtml) ??
    DEFAULT_FROZEN_DELIVERY_CALCULATOR_BODY_HTML;

  return resolvedBodyHtml.split(FROZEN_DELIVERY_CALCULATOR_PRICE_TOKEN).join(
    pricePerBoxLabel ?? "an unavailable amount",
  );
}

export function resolveFrozenDeliveryCalculatorIntro(
  source: FrozenDeliveryCalculatorIntroSource,
  pricePerBoxLabel: string | null,
  fallbackImageUrl: string,
): FrozenDeliveryCalculatorIntro {
  const resolvedImage = normalizeMediaAsset(source.image);

  return {
    eyebrow:
      normalizeOptionalText(source.eyebrow) ??
      DEFAULT_FROZEN_DELIVERY_CALCULATOR_EYEBROW,
    headline:
      normalizeOptionalText(source.headline) ??
      DEFAULT_FROZEN_DELIVERY_CALCULATOR_HEADLINE,
    bodyHtml: populateFrozenDeliveryCalculatorBodyHtml(
      source.bodyHtml,
      pricePerBoxLabel,
    ),
    image: resolvedImage
      ? {
          ...resolvedImage,
          alt:
            normalizeOptionalText(resolvedImage.alt) ??
            DEFAULT_FROZEN_DELIVERY_CALCULATOR_IMAGE_ALT,
        }
      : {
          url: fallbackImageUrl,
          alt: DEFAULT_FROZEN_DELIVERY_CALCULATOR_IMAGE_ALT,
        },
  };
}

export function buildFrozenDeliveryResultCopy(
  summary: Pick<
    FrozenDeliveryQuoteSummary,
    | "boxesNeeded"
    | "formattedDeliveryCost"
    | "formattedPricePerBox"
    | "hasItems"
  >,
) {
  if (!summary.hasItems) {
    return null;
  }

  if (summary.formattedDeliveryCost) {
    return {
      primary: `You would pay ${summary.formattedDeliveryCost} to have this order delivered to any address in the UK.`,
      secondary: summary.formattedPricePerBox
        ? `Based on ${summary.boxesNeeded} ${summary.boxesNeeded === 1 ? "box" : "boxes"} at ${summary.formattedPricePerBox} per box.`
        : null,
    };
  }

  return {
    primary:
      "We can work out how many boxes you need, but the delivery price is currently unavailable.",
    secondary: null,
  };
}
